import LettuceTalkLogo from "../../images/logo.png";
import SpeechBubble from "../../components/SpeechBubble";
import { trackEvent } from "../../utils/Analytics";
import { useAuth } from '../../contexts/AuthContext';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../configs/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";

// Flip to false when custom deck creation and purchase checkout are live again.
const CUSTOM_DECK_MAINTENANCE_MODE = true;
const customDeckMaintenanceClass = CUSTOM_DECK_MAINTENANCE_MODE
  ? "opacity-60 cursor-not-allowed"
  : "";

function CustomizeIntroView() {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [hasCredit, setHasCredit] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingCredit, setCheckingCredit] = useState(true);
  const navigate = useNavigate();

  // Admin emails list - customize as needed
  const ADMIN_EMAILS = [
    "hello@imoonshots.com",
    "michelle@imoonshots.com",
    "creashells@gmail.com",
    // Add more admin emails here
  ];

  const TEST_MODE = false;
  const CUSTOMIZE_STRIPE_PRICE_ID = TEST_MODE ? process.env.REACT_APP_CUSTOM_DIGITAL_PRICE_ID_DEV : process.env.REACT_APP_CUSTOM_DIGITAL_PRICE_ID_PROD;

  const successUrl = `${window.location.origin}/purchase/success?session_id={CHECKOUT_SESSION_ID}`;
  const cancelUrl = window.location.href;

  useEffect(() => {
    const checkCredit = async () => {
      if (!currentUser) return;

      // Check if user is admin
      const adminStatus = ADMIN_EMAILS.includes(currentUser.email);
      setIsAdmin(adminStatus);

      if (adminStatus) {
        // Admins automatically have access
        setHasCredit(true);
        setCheckingCredit(false);
        return;
      }

      const userDocRef = doc(db, "users", currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.exists() ? userDocSnap.data() : {};
      if (userData.customDeckCredits > 0) {
        setHasCredit(true);
      }
      setCheckingCredit(false);
    };
    checkCredit();
  }, [currentUser]);

  const handleStartClick = async () => {
    if (CUSTOM_DECK_MAINTENANCE_MODE) {
      return;
    }

    if (!currentUser) {
      alert("You must be signed in to create a custom deck.");
      return;
    }

    setIsLoading(true);

    trackEvent('Deck', 'Start Deck Creation', 'User started deck creation', null, {
      user_id: currentUser?.uid,
      user_email: currentUser?.email,
      referrer: document.referrer,
      current_url: window.location.href,
    });

    try {
      const token = await currentUser.getIdToken();

      const res = await fetch(process.env.REACT_APP_CREATE_STRIPE_SESSION_URL_PROD, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          priceId: CUSTOMIZE_STRIPE_PRICE_ID,
          successUrl,
          cancelUrl,
          deckType: "custom-digital",
        }),
        mode: "cors",
      });

      const result = await res.json();

      if (!res.ok) {
        alert(result.error || "Something went wrong. Please try again.");
        setIsLoading(false);
        return;
      }

      window.location.href = result.url;
    } catch (err) {
      console.error("Error starting checkout:", err);
      alert("Something went wrong. Please try again.");
      setIsLoading(false);
    }
  };

  const FULL_PRICE = 14.99;
  const DISCOUNTED_PRICE = 3.99; // 86% off

  if (checkingCredit) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-3 border-green-200 border-t-green-500 rounded-full animate-spin" />
          <p className="text-gray-600 font-medium">Checking your credits...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 flex flex-col justify-content items-center text-center">
      <img className="w-28" src={LettuceTalkLogo} alt="LettuceTalk Logo" />

      <SpeechBubble
        header="Welcome to LettuceTalk's AI Kitchen!"
        subtitle="In the following sections, we'll walk you through three questions to create a custom LettuceTalk deck for your family."
      />

      {/* Discount note with direct price */}
      {!isAdmin && !CUSTOM_DECK_MAINTENANCE_MODE && (
        <div className="mt-4 text-sm text-gray-600 mx-4">
          <span className="font-bold">Limited Early Access Promotion!</span>
        </div>
      )}

      {CUSTOM_DECK_MAINTENANCE_MODE && (
        <div className="mt-6 mx-4 max-w-md rounded-2xl border border-[#4D7C0F]/20 bg-[#F8F8F2] px-5 py-4 text-center text-[#3F3F46] shadow-sm">
          <p className="text-sm font-bold text-[#4D7C0F]">
            Scheduled Maintenance
          </p>
          <p className="mt-2 text-sm leading-relaxed">
            Custom deck creation and purchases are temporarily paused while we
            update the LettuceTalk app experience. Your account and existing
            decks remain available. Thank you for your patience, we can’t wait to share what we’re cooking up!
          </p>
        </div>
      )}

      {/* Show unused credit prompt below the SpeechBubble */}
      {hasCredit && (
        <div className="mt-6">
          {!isAdmin && (
            <p>You have an unused custom deck credit!</p>
          )}
          <button
            className={`btn btn-primary mt-4 h-auto w-[min(90vw,360px)] whitespace-normal px-4 py-3 leading-snug ${customDeckMaintenanceClass}`}
            onClick={() => navigate("/customize/questions")}
            disabled={CUSTOM_DECK_MAINTENANCE_MODE}
          >
            {CUSTOM_DECK_MAINTENANCE_MODE
              ? "Customization temporarily unavailable"
              : isAdmin
                ? "Start AI Customization"
                : "Use My Credit"}
          </button>

          {!isAdmin && !CUSTOM_DECK_MAINTENANCE_MODE && (
            <button
              className={`mt-4 btn bg-rainbow-pastel flex items-center justify-center gap-2 mx-auto ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
              onClick={handleStartClick}
              disabled={isLoading}
            >
              {isLoading && (
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
              )}
              {!isLoading && "Buy Another Custom Deck Credit"}
            </button>
          )}
        </div>
      )}

      {/* Only show purchase UI if user does not have credit */}
      {!hasCredit && (
        <>
          <button
            className={`mt-4 btn btn-primary flex h-auto w-[min(90vw,390px)] items-center justify-center gap-2 whitespace-normal px-4 py-3 leading-snug ${isLoading ? 'opacity-60 cursor-not-allowed' : customDeckMaintenanceClass}`}
            onClick={handleStartClick}
            disabled={isLoading || CUSTOM_DECK_MAINTENANCE_MODE}
          >
            {isLoading && (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            )}
            {!isLoading && CUSTOM_DECK_MAINTENANCE_MODE && (
              "Custom deck purchases temporarily unavailable"
            )}
            {!isLoading && !CUSTOM_DECK_MAINTENANCE_MODE && (
              <>
                Create Your Custom Deck —
                <span className="line-through text-gray-400">${FULL_PRICE.toFixed(2)}</span>
                <span className="text-lg text-black">${DISCOUNTED_PRICE}</span>
              </>
            )}
          </button>
        </>
      )}
    </div>
  );
}

export default CustomizeIntroView;
